import React, { useState } from "react";
import PageHero from "@/components/shared/PageHero";
import { FormShell, SuccessPopup } from "@/components/shared/FormShell";
import { DISTRICTS, CATEGORIES } from "@/data/mock";
import { Upload, MapPin } from "lucide-react";

// Ensure select options have explicit value attributes for consistency
export default function Grievance() {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ district: "", constituency: "", category: "", description: "", location: "" });
    const code = "MWK-2026-" + Math.floor(100000 + Math.random() * 900000);
    return (
        <>
            <PageHero
                testId="grievance-page"
                eyebrow="Submit a Grievance"
                chapter="Service · 02"
                title="Submit a grievance."
                subtitle="Raise community or civic concerns with supporting context. Every submission is routed to a district cell for accountable follow-up."
            />
            <FormShell
                aside={
                    <div>
                        <div className="eyebrow mb-4">Handled with care</div>
                        <p className="text-[14px] leading-relaxed text-slate-600">
                            Grievances travel through a numbered timeline — Submitted → Assigned → Under Review →
                            Resolved. You&apos;ll be notified at each step.
                        </p>
                        <div className="mt-6 rounded-2xl border border-ink/10 bg-white p-4 text-[12px] leading-relaxed text-slate-500">
                            <strong className="text-ink">Tip:</strong> Include specifics — street name, landmark, time
                            of day — for faster field verification.
                        </div>
                    </div>
                }
            >
                <form className="space-y-8" onSubmit={(e) => { e.preventDefault(); setOpen(true); }} data-testid="grievance-form">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="field">
                            <label>District</label>
                            <select required value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="gr-district">
                                <option value="">Select district</option>
                                {DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <div className="field">
                            <label>Constituency</label>
                            <input required value={form.constituency} onChange={(e) => setForm({ ...form, constituency: e.target.value })} placeholder="Your constituency" data-testid="gr-constituency" />
                        </div>
                        <div className="field">
                            <label>Issue category</label>
                            <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="gr-category">
                                <option value="">Select category</option>
                                {CATEGORIES.grievance.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="field">
                            <label>Location</label>
                            <div className="relative">
                                <input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Street, ward or landmark" data-testid="gr-location" />
                                <MapPin className="pointer-events-none absolute right-0 top-3 h-4 w-4 text-pink" />
                            </div>
                        </div>
                    </div>
                    <div className="field">
                        <label>Description</label>
                        <textarea rows={6} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the issue with as much specificity as possible." data-testid="gr-description" />
                    </div>
                    <div className="field">
                        <label>Upload images</label>
                        <label className="mt-2 flex cursor-pointer items-center justify-between rounded-2xl border border-dashed border-ink/20 bg-slate-50 px-6 py-6 text-[13px] text-slate-500 hover:border-pink hover:text-pink transition-colors">
                            <span className="flex items-center gap-3"><Upload className="h-4 w-4" /> Attach photos of the issue (UI only)</span>
                            <span className="text-[10px] uppercase tracking-[0.24em]">Optional</span>
                            <input type="file" className="hidden" multiple data-testid="gr-upload" />
                        </label>
                    </div>
                    <div className="flex items-center gap-4 pt-4">
                        <button type="submit" className="btn-primary" data-testid="gr-submit">Submit grievance</button>
                    </div>
                </form>
            </FormShell>
            <SuccessPopup
                open={open}
                onClose={() => setOpen(false)}
                title="Grievance received"
                body="Your grievance has been logged. A district officer will review and respond via the tracking timeline."
                code={code}
            />
        </>
    );
}
